/**
 * Date formatting utilities for consistent DD.MM.YYYY display
 */

/**
 * Convert ISO date string (YYYY-MM-DD) to DD.MM.YYYY format
 * @param {string} isoDate - Date in YYYY-MM-DD format
 * @returns {string} Date in DD.MM.YYYY format
 */
export function formatDateDDMMYYYY(isoDate) {
  if (!isoDate) return '';

  const date = new Date(isoDate + 'T00:00:00'); // Add time to avoid timezone issues
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

/**
 * Convert Firebase Timestamp to DD.MM.YYYY format
 * @param {Object} timestamp - Firebase Timestamp object
 * @returns {string} Date in DD.MM.YYYY format
 */
export function formatTimestampDDMMYYYY(timestamp) {
  if (!timestamp || !timestamp.toDate) return '';

  const date = timestamp.toDate();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

/**
 * Get today's date in YYYY-MM-DD format (for comparisons and input fields)
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' });
}

/**
 * Safe: any Firestore Timestamp, Date, ISO string or null -> YYYY-MM-DD or null.
 * Never throws — returns null for unknown values.
 */
export function toISODate(value) {
  try {
    if (!value) return null;
    if (typeof value.toDate === 'function') {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString().split('T')[0];
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Convert Firebase Timestamp to DD.MM.YYYY HH:MM format (date and time)
 * @param {Object} timestamp - Firebase Timestamp object
 * @returns {string} Date and time in DD.MM.YYYY HH:MM format
 */
export function formatTimestampDDMMYYYYHHMM(timestamp) {
  if (!timestamp || !timestamp.toDate) return '';

  const date = timestamp.toDate();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Convert ISO date string to Date object at midnight local time
 * Kept for backward compat with existing tests/callers.
 */
export function isoToDate(isoDate) {
  return new Date(isoDate + 'T00:00:00');
}
